import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useForumContext } from '../context/ForumContext';
import { usePageVisitTracking } from '../hooks/usePageVisitTracking';
import { getQuestionById } from '../services/firebase/forum';
import Container from '../components/common/Container';
import Button from '../components/common/Button';
import LoadingSkeleton from '../components/common/LoadingSkeleton';
import QuestionDetail from '../components/features/forum/QuestionDetail';
import ReplyList from '../components/features/forum/ReplyList';
import ReplyModal from '../components/features/forum/ReplyModal';
import ReplyComposer from '../components/features/forum/ReplyComposer';
import OriginalPostPreview from '../components/features/forum/OriginalPostPreview';
import useReplyModal from '../hooks/useReplyModal';
import { ChatBubbleLeftRightIcon, UserIcon } from '@heroicons/react/24/outline';

const QuestionDetailPage = () => {
  const { questionId } = useParams();
  const { user, userProfile } = useAuthContext();
  const navigate = useNavigate();
  const replyListRef = useRef();
  
  
  // Track page visits for notifications
  usePageVisitTracking();
  
  // Get forum context for optimistic updates
  const { updateQuestion } = useForumContext();
  
  // State management for question data
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Reply modal functionality
  const { isOpen, originalPost, openModal, closeModal } = useReplyModal();

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Load question data and increment view count
  useEffect(() => {
    const loadQuestion = async () => {
      if (!questionId || !user) return;

      setLoading(true);
      setError(null);

      try {
        const result = await getQuestionById(questionId, user.uid);
        
        if (result.success) {
          setQuestion(result.data);
        } else {
          setError(result.error);
        }
      } catch (error) {
        console.error('Error loading question:', error);
        setError('Failed to load question');
      } finally {
        setLoading(false);
      }
    };

    loadQuestion();
  }, [questionId, user]);



  const handleLikeUpdate = (questionId, newLikeCount, isLiked) => {
    // Update question like count locally
    setQuestion(prev => prev ? { ...prev, likeCount: newLikeCount } : null);
    // Also update in the forum context for when user navigates back
    updateQuestion(questionId, { likeCount: newLikeCount });
  };

  const handleReplySubmitted = (newReply, tempIdToReplace = null, error = null) => {
    // Add reply to the list via ref
    if (replyListRef.current) {
      replyListRef.current.addReply(newReply, tempIdToReplace, error);
    }
    
    // Only update reply count for new replies (not replacements or errors)
    if (!tempIdToReplace && !error && newReply) {
      const newReplyCount = (question?.replyCount || 0) + 1;
      setQuestion(prev => prev ? { ...prev, replyCount: newReplyCount } : null);
      updateQuestion(questionId, { replyCount: newReplyCount });
    }
  };

  const handleReplyCountUpdate = (count) => {
    setQuestion(prev => prev ? { ...prev, replyCount: count } : null);
  };

  const handleQuestionDelete = (questionId) => {
    console.log('handleQuestionDelete called for questionId:', questionId);
    // Navigate back to forum when question is deleted
    navigate('/forum');
  };

  const handleOpenReplyModal = () => {
    if (!user || !question) return;
    
    openModal({
      id: question.id,
      type: 'question',
      title: question.title,
      content: question.content,
      authorName: question.authorName,
      authorPhotoURL: question.authorPhotoURL,
      authorId: question.authorId,
      createdAt: question.createdAt
    });
  };

  if (!user) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <div className="h-12 mb-8 flex justify-center">
              <svg width="156" height="48" viewBox="0 0 156 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.067 12.161C25.067 13.172 24.5 14.075 23.659 14.6C24.5 15.124 25.067 16.027 25.067 17.04V18.617C25.067 20.378 23.637 21.806 21.881 21.806H14.987V7.398H21.881C23.637 7.398 25.067 8.825 25.067 10.588V12.161ZM21.881 16.456C21.881 15.576 21.168 14.863 20.286 14.863H18.173V17.972H20.286C21.168 17.972 21.881 17.335 21.881 16.456ZM21.881 12.736C21.881 11.855 21.168 11.143 20.286 11.143H18.173V14.252H20.286C21.168 14.252 21.881 13.615 21.881 12.736ZM31.061 21.817H27.943V7.395H31.061V21.817ZM36.112 7.398H46.221V11.143H39.298V14.025H45.454V16.995H39.298V17.989H46.221V21.806H36.112V7.398ZM59.227 21.923C58.046 21.923 56.951 21.605 56.017 21.044L54.829 22.231L52.516 19.915L53.704 18.729C53.143 17.793 52.823 16.701 52.823 15.517C52.823 14.334 53.143 13.242 53.704 12.306L52.516 11.12L54.829 8.804L56.017 9.991C56.951 9.43 58.046 9.112 59.227 9.112C62.642 9.112 65.397 11.868 65.397 15.281C65.397 18.695 62.642 21.923 59.227 21.923ZM59.227 12.858C57.782 12.858 56.598 14.04 56.598 15.487C56.598 16.934 57.782 18.114 59.227 18.114C60.672 18.114 61.856 16.934 61.856 15.487C61.856 14.040 60.672 12.858 59.227 12.858ZM76.142 7.458V18.304C76.142 19.597 75.09 20.647 73.8 20.647H71.459C70.168 20.647 69.117 19.597 69.117 18.304V7.458H66V18.304C66 21.348 68.417 23.766 71.459 23.766H73.8C76.843 23.766 79.26 21.348 79.26 18.304V7.458H76.142ZM82.856 21.817H86.005V7.395H82.856V21.817ZM93.636 11.231H89.831V7.458H97.429C98.72 7.458 99.772 8.507 99.772 9.772V17.608C99.772 19.478 98.309 21.008 96.502 21.201L94.935 21.367V19.648C91.952 19.648 93.636 21.817 93.636 21.817H90.518V13.812C90.518 12.374 91.785 11.231 93.636 11.231ZM96.624 17.573V11.231H93.753V17.573H96.624ZM101.756 21.817H105.085V7.395H101.756V21.817ZM109.928 7.398H120.037V11.143H113.114V14.025H119.27V16.995H113.114V17.989H120.037V21.806H109.928V7.398ZM129.045 7.398H132.164V17.632H137.448V21.806H129.045V7.398Z" fill="#3a0ca3"/>
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">Please log in</h1>
            <p className="mt-4 text-lg text-gray-600">You need to be logged in to view this page.</p>
            <Button as="a" href="/" className="mt-8">Return to Homepage</Button>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        {/* Loading indicator at top */}
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gradient-to-r from-purple-500 to-pink-500">
          <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse" />
        </div>
        
        <Container>
          <div className="py-8 sm:py-10 lg:py-12">
            {/* Loading State with better visual feedback */}
            <div className="space-y-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="animate-pulse">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
                  </div>
                </div>
              </div>
              <LoadingSkeleton className="h-32" />
              <LoadingSkeleton className="h-48" />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <Container>
          <div className="py-8 sm:py-10 lg:py-12">
            {/* Error State */}
            <div className="text-center py-16">
              <div className="mx-auto max-w-md">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Question Not Found
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  {error}
                </p>
                <Button onClick={() => navigate('/forum')}>
                  Return to Forum
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <Container>
        <div className="py-8 sm:py-10 lg:py-12">
          {/* Integrated Forum Thread */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Question Detail */}
              <div className="p-6">
                <QuestionDetail 
                  question={question} 
                  onLikeUpdate={handleLikeUpdate}
                  onReplySubmitted={handleReplySubmitted}
                  onDelete={handleQuestionDelete}
                />
              </div>

              {/* Reply Section */}
              <div className="bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                {/* Section Header */}
                <div className="px-6 pt-6 pb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    Discussion ({question?.replyCount || 0} {question?.replyCount === 1 ? 'reply' : 'replies'})
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Share your insights, ask questions, or engage with the community
                  </p>
                </div>

                {/* Replies List */}
                <div className="px-6 pb-6">
                  <ReplyList 
                    ref={replyListRef}
                    questionId={questionId}
                    questionAuthorId={question?.authorId}
                    onReplyCountUpdate={handleReplyCountUpdate}
                    onReplySubmitted={handleReplySubmitted}
                  />
                </div>

                {/* Add Comment Button */}
                <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {user ? (
                    <div 
                      className="reply-prompt py-4 px-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-700 cursor-pointer transition-all duration-200 hover:from-purple-100 hover:to-blue-100 dark:hover:from-purple-800/30 dark:hover:to-blue-800/30 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md active:bg-purple-100 dark:active:bg-purple-800/30 touch-manipulation"
                      onClick={handleOpenReplyModal}
                      role="button"
                      tabIndex={0}
                      aria-label="Open reply modal"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenReplyModal();
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        {(() => {
                          const profilePictureUrl = userProfile?.profilePicture || userProfile?.basicInfo?.photoURL || user.photoURL;
                          return profilePictureUrl ? (
                            <img
                              src={profilePictureUrl}
                              alt={user.displayName || 'You'}
                              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover flex-shrink-0 ring-2 ring-purple-200 dark:ring-purple-700"
                            />
                          ) : (
                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0 ring-2 ring-purple-200 dark:ring-purple-700">
                              <UserIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <span className="text-gray-700 dark:text-gray-300 text-sm font-medium block">
                            💬 Share your thoughts and join the discussion
                          </span>
                          <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">
                            Click here to write a reply →
                          </span>
                        </div>
                        <div className="flex-shrink-0">
                          <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="reply-prompt-logged-out py-6 px-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-700 text-center">
                      <div className="mb-3">
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-500 dark:text-purple-400 mx-auto" />
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">
                        💬 Want to join the discussion?
                      </p>
                      <p className="text-gray-500 dark:text-gray-400 text-xs mb-4">
                        Log in to share your thoughts and engage with the community
                      </p>
                      <Button 
                        as="a" 
                        href="/auth" 
                        size="sm" 
                        className="touch-manipulation min-h-[44px] sm:min-h-auto bg-purple-600 hover:bg-purple-700 text-white border-0"
                      >
                        Log In to Reply
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Reply Modal */}
      <ReplyModal
        isOpen={isOpen}
        onClose={closeModal}
        title="Reply to Post"
      >
        {originalPost && (
          <>
            <OriginalPostPreview post={originalPost} />
            <ReplyComposer
              questionId={questionId}
              onSubmit={(reply, tempIdToReplace, error) => {
                handleReplySubmitted(reply, tempIdToReplace, error);
                // Only close modal if it's not an error
                if (!error) {
                  closeModal();
                }
              }}
              onCancel={closeModal}
              autoFocus={true}
            />
          </>
        )}
      </ReplyModal>
    </div>
  );
};

export default QuestionDetailPage;